import { CDN_URL, isSfwSite } from '../../config.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { storage } from '../../utils/storage.js';
import { perkTierForUser } from '../../utils/membershipUsage.js';
import { getCachedOpenRouterKeyConfigured, refreshOpenRouterKeyStatus } from '../../utils/openRouterKeyStatus.js';
import { getNsfwEnabled } from '../../utils/userPrefs.js';
import { findPriceAnchor } from './model-price.js';
import {
  FREE_STYLE_MAX_MULTIPLIER,
  STARTER_STYLE_MAX_MULTIPLIER,
  accessLaneFromMultiplier,
  isLaneAllowedForTier,
  isMultiplierAllowedForMembership,
  skipsModelLaneGate
} from '../../shared/model-access.js';

export {
  FREE_STYLE_MAX_MULTIPLIER,
  STARTER_STYLE_MAX_MULTIPLIER,
  accessLaneFromMultiplier,
  isMultiplierAllowedForMembership,
  skipsModelLaneGate
};

export const CHARACTER_POSITIONS = {
    single: [
        { left: '35%', right: 'auto', transform: 'translateX(0)' }  // Centered slightly left
    ],
    dual: [
        { left: '15%', right: 'auto', transform: 'translateX(0)' },  // Closer to center for better composition
        { right: '5%', left: 'auto', transform: 'translateX(0)' }    // Keep right side as is - it's well positioned
    ],
    triple: [
        { left: '10%', right: 'auto', transform: 'translateX(0)' },         // Closer to center for better composition
        { right: '10%', left: 'auto', transform: 'translateX(0)' },         // Closer to center for better composition
        { left: '50%', right: 'auto', transform: 'translateX(-50%)' }  // Center
    ]
};

// Character image sizing baseline for first-paint CLS stability
export const CHARACTER_SIZES = {
    normal: { width: 'auto', height: '900px' }
};

export const Z_INDEX = {
    characters: 50
};

// Vibe options for AI response style (used by character-info modal and message-utils)

let cachedAllStyles = null;

const isActiveStyle = (style) => Boolean(style?.name);

const resolvePromptId = (promptId, styles) => {
  if (!Array.isArray(styles) || promptId == null || promptId === '') return promptId;

  let id = Number(promptId);
  if (!Number.isFinite(id)) return promptId;

  const byId = (n) => styles.find((style) => Number(style.id) === n);

  for (let i = 0; i < 8; i++) {
    const entry = byId(id);
    if (!entry?.replaced_by) return id;
    id = Number(entry.replaced_by);
    if (!Number.isFinite(id)) return promptId;
  }

  return id;
};

async function loadAllResponseStyles() {
  if (cachedAllStyles) return cachedAllStyles;
  try {
    const data = await fetchCache.fetchJSON(`${CDN_URL}/data/response_styles.json`);
    if (Array.isArray(data) && data.length > 0) {
      cachedAllStyles = data;
      return cachedAllStyles;
    }
  } catch (e) {
    console.warn('Failed to fetch response_styles from CDN, using fallback:', e);
  }
  return null;
}

export async function getVibeOptions() {
  const allStyles = await loadAllResponseStyles();
  if (!Array.isArray(allStyles)) return null;
  const nsfwAllowed = getNsfwEnabled();
  return allStyles.filter((style) => isActiveStyle(style) && (nsfwAllowed || style.is_nsfw !== true));
}

/**
 * Weighted USD per 1M tokens for the 1x style. Includes NSFW styles so the anchor
 * stays available when the picker hides them.
 * @returns {Promise<number|null>}
 */
export async function getResponseStylePriceAnchor() {
  return findPriceAnchor(await loadAllResponseStyles());
}

export const OPENROUTER_MODEL_SLUG = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._:-]+$/;

/**
 * Saved custom OpenRouter slug, or null when unset.
 * @returns {string | null}
 */
export const getUserModelOverride = () => {
  const stored = storage.get('selectedModelOverride');
  if (typeof stored !== 'string') return null;
  const trimmed = stored.trim();
  return OPENROUTER_MODEL_SLUG.test(trimmed) ? trimmed : null;
};

/**
 * Persist a confirmed slug, or clear the override.
 * @param {string | null | undefined} slug
 */
export const setUserModelOverride = (slug) => {
  if (typeof slug === 'string' && OPENROUTER_MODEL_SLUG.test(slug.trim())) {
    storage.set('selectedModelOverride', slug.trim());
    return;
  }
  storage.set('selectedModelOverride', '');
};

/**
 * Public OpenRouter catalog lookup. No API key.
 * @param {string} slug
 * @returns {Promise<{ ok: true, id: string, name: string, reasoningCapable: boolean, pricing: object | null } | { ok: false, reason: 'format' | 'missing' | 'not_text' | 'unreachable' }>}
 */
export const lookupOpenRouterTextModel = async (slug) => {
  const trimmed = String(slug || '').trim();
  if (!OPENROUTER_MODEL_SLUG.test(trimmed)) return { ok: false, reason: 'format' };
  const slash = trimmed.indexOf('/');
  const url = `https://openrouter.ai/api/v1/model/${encodeURIComponent(trimmed.slice(0, slash))}/${encodeURIComponent(trimmed.slice(slash + 1))}`;
  try {
    const response = await fetch(url);
    if (response.status === 404) return { ok: false, reason: 'missing' };
    if (!response.ok) return { ok: false, reason: 'unreachable' };
    const body = await response.json();
    const data = body?.data && typeof body.data === 'object' ? body.data : body;
    const outputs = data?.architecture?.output_modalities;
    if (!Array.isArray(outputs) || !outputs.includes('text')) return { ok: false, reason: 'not_text' };
    const params = Array.isArray(data.supported_parameters) ? data.supported_parameters : [];
    return {
      ok: true,
      id: typeof data.id === 'string' && data.id ? data.id : trimmed,
      name: typeof data.name === 'string' ? data.name : trimmed,
      reasoningCapable: params.includes('reasoning'),
      pricing: data.pricing && typeof data.pricing === 'object' ? data.pricing : null
    };
  } catch {
    return { ok: false, reason: 'unreachable' };
  }
};

const DEFAULT_VIBE_ID = 28;
const DEFAULT_SFW_VIBE_ID = 10;

export const PREMIUM_STYLE_TIERS = new Set(['single', 'harem_protagonist']);

export const modelLaneSkipped = () => skipsModelLaneGate({
  sitePolicy: isSfwSite() ? 'sfw' : 'full',
  usesUserOpenRouterKey: getCachedOpenRouterKeyConfigured() === true
});

const getMembershipTier = () => perkTierForUser(storage.get('userData') || {});

const parseStylePriceMultiplier = (input) => {
  if (input == null || input === '') return NaN;
  if (typeof input === 'number') return Number.isFinite(input) ? input : NaN;

  const str = String(input).trim().toLowerCase();
  if (str === 'free') return 1;

  const cleaned = str.replace(/x$/i, '');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
};

/**
 * Access lane from a style's credit multiplier.
 * @param {object|null|undefined} style
 * @returns {'included' | 'plus' | 'max'}
 */
export const getStyleAccessLane = (style) => {
  if (!isActiveStyle(style)) return 'max';
  return accessLaneFromMultiplier(parseStylePriceMultiplier(style?.price));
};

/**
 * @param {object|null|undefined} style
 * @param {string|null|undefined} membershipTier
 * @returns {boolean}
 */
export const isStyleAllowedForMembership = (style, membershipTier) => {
  if (!isActiveStyle(style)) return false;
  const tier = String(membershipTier || 'free').trim().toLowerCase();
  return isLaneAllowedForTier(getStyleAccessLane(style), tier);
};

const clampVibeIdForMembership = (promptId, styles, membershipTier, skipMembershipGate = false) => {
  const resolved = resolvePromptId(promptId, styles);
  if (skipMembershipGate) return Number(resolved);
  const list = Array.isArray(styles) ? styles : [];
  const byId = (n) => list.find((style) => Number(style.id) === Number(n));
  const isAllowed = (style) => isStyleAllowedForMembership(style, membershipTier);
  if (isAllowed(byId(resolved))) return Number(resolved);
  if (isAllowed(byId(DEFAULT_VIBE_ID))) return DEFAULT_VIBE_ID;
  const firstAllowed = list.find(isAllowed);
  return firstAllowed?.id != null ? Number(firstAllowed.id) : DEFAULT_VIBE_ID;
};

const clampVibeIdForNsfw = (promptId, styles, nsfwAllowed) => {
  const resolved = Number(resolvePromptId(promptId, styles));
  if (nsfwAllowed) return resolved;
  const list = Array.isArray(styles) ? styles : [];
  const byId = (n) => list.find((style) => Number(style.id) === Number(n));
  const style = byId(resolved);
  if (style?.is_nsfw !== true) return resolved;
  const sfwDefault = list.find((entry) => entry?.default && entry.is_nsfw !== true && isActiveStyle(entry));
  if (sfwDefault?.id != null) return Number(sfwDefault.id);
  if (isActiveStyle(byId(DEFAULT_SFW_VIBE_ID)) && byId(DEFAULT_SFW_VIBE_ID).is_nsfw !== true) {
    return DEFAULT_SFW_VIBE_ID;
  }
  const firstSfw = list.find((entry) => isActiveStyle(entry) && entry.is_nsfw !== true);
  return firstSfw?.id != null ? Number(firstSfw.id) : DEFAULT_SFW_VIBE_ID;
};

/**
 * Returns the user's selected vibe ID. If none is stored, randomly assigns one from styles
 * marked `default: true` (A/B pool) and persists it. Falls back to all active styles if no
 * defaults are marked, then to DEFAULT_VIBE_ID if options cannot be fetched.
 * Paid-only stored vibes are not overwritten for guests/free users (upgrade restores the pick).
 * @returns {Promise<number>}
 */
export async function getUserVibe() {
  await refreshOpenRouterKeyStatus();
  const allStyles = await loadAllResponseStyles();
  const membershipTier = getMembershipTier();
  const skipMembershipGate = modelLaneSkipped();
  const nsfwAllowed = getNsfwEnabled();
  const stored = storage.get('selectedVibeId');
  if (stored != null && stored !== '') {
    const parsed = parseInt(stored, 10);
    if (!Number.isNaN(parsed)) {
      if (Array.isArray(allStyles) && allStyles.length > 0) {
        const resolved = resolvePromptId(parsed, allStyles);
        const resolvedStyle = allStyles.find((style) => Number(style.id) === Number(resolved));
        if (resolved !== parsed && isStyleAllowedForMembership(resolvedStyle, membershipTier)) {
          storage.set('selectedVibeId', resolved);
        }
        const membershipClamped = clampVibeIdForMembership(resolved, allStyles, membershipTier, skipMembershipGate);
        const nextId = clampVibeIdForNsfw(membershipClamped, allStyles, nsfwAllowed);
        if (nextId !== parsed) storage.set('selectedVibeId', nextId);
        return nextId;
      }
      return parsed;
    }
  }
  const options = await getVibeOptions();
  if (Array.isArray(options) && options.length > 0) {
    const eligible = options.filter((o) => o.default && (nsfwAllowed ? o.is_nsfw === true : o.is_nsfw !== true));
    const basePool = eligible.length > 0 ? eligible : options;
    const allowedPool = skipMembershipGate
      ? basePool
      : basePool.filter((o) => isStyleAllowedForMembership(o, membershipTier));
    const pickFrom = allowedPool.length > 0
      ? allowedPool
      : options.filter((o) => skipMembershipGate || isStyleAllowedForMembership(o, membershipTier));
    const random = pickFrom[Math.floor(Math.random() * pickFrom.length)];
    if (random?.id != null) {
      storage.set('selectedVibeId', random.id);
      return Number(random.id);
    }
  }
  return nsfwAllowed ? DEFAULT_VIBE_ID : DEFAULT_SFW_VIBE_ID;
}

export const REASONING_EFFORTS = ['none', 'minimal', 'low', 'medium'];
export const DEFAULT_REASONING_EFFORT = 'none';
export const DEFAULT_TEMPERATURE = 1;
export const DEFAULT_MAX_TOKENS = 150;
export const MIN_MAX_TOKENS = 50;
export const MAX_MAX_TOKENS = 400;
export const DEFAULT_CONTEXT_TURNS = 8;
export const MIN_CONTEXT_TURNS = 1;
export const MAX_CONTEXT_TURNS = 16;
export const CUSTOM_PROMPT_MAX_LENGTH = 500;
export const CUSTOM_PROMPT_NAME_MAX_LENGTH = 50;

export async function getUserReasoningEffort() {
  const stored = storage.get('selectedReasoningEffort');
  if (typeof stored === 'string' && REASONING_EFFORTS.includes(stored)) return stored;
  return DEFAULT_REASONING_EFFORT;
}

export async function getUserTemperature() {
  const stored = storage.get('selectedTemperature');
  const parsed = typeof stored === 'number' ? stored : parseFloat(stored);
  if (!Number.isFinite(parsed)) return DEFAULT_TEMPERATURE;
  return Math.min(1, Math.max(0, parsed));
}

const parseStoredNumber = (stored) => {
  if (typeof stored === 'number') return stored;
  if (typeof stored === 'string' && stored.trim() !== '') return parseFloat(stored);
  return NaN;
};

export async function getUserMaxTokens() {
  const parsed = parseStoredNumber(storage.get('selectedMaxTokens'));
  if (!Number.isFinite(parsed)) return DEFAULT_MAX_TOKENS;
  return Math.min(MAX_MAX_TOKENS, Math.max(MIN_MAX_TOKENS, Math.round(parsed)));
}

export async function getUserContextTurns() {
  const parsed = parseStoredNumber(storage.get('selectedContextTurns'));
  if (!Number.isFinite(parsed)) return DEFAULT_CONTEXT_TURNS;
  return Math.min(MAX_CONTEXT_TURNS, Math.max(MIN_CONTEXT_TURNS, Math.round(parsed)));
}

export async function getUserTopP() {
  const parsed = parseStoredNumber(storage.get('selectedTopP'));
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.min(1, Math.max(0, parsed));
}

export async function getUserFrequencyPenalty() {
  const parsed = parseStoredNumber(storage.get('selectedFrequencyPenalty'));
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.min(2, Math.max(0, parsed));
}

export async function getUserPresencePenalty() {
  const parsed = parseStoredNumber(storage.get('selectedPresencePenalty'));
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.min(2, Math.max(0, parsed));
}

export async function getUserCustomPrompt() {
  const stored = storage.get('selectedCustomPrompt');
  if (typeof stored !== 'string') return null;
  const trimmed = stored.trim().slice(0, CUSTOM_PROMPT_MAX_LENGTH);
  return trimmed.length > 0 ? trimmed : null;
}

export async function getUserTextGenerationParams() {
  return {
    maxTokens: await getUserMaxTokens(),
    contextTurns: await getUserContextTurns(),
    topP: await getUserTopP(),
    frequencyPenalty: await getUserFrequencyPenalty(),
    presencePenalty: await getUserPresencePenalty(),
    customPrompt: await getUserCustomPrompt()
  };
}
